import { AbstractStorage } from './abstractStorage';
import { mergeDeep } from '../utils';

export class LocalStorage<T> extends AbstractStorage<T> {
  protected storage = localStorage;

  delete(key: string): boolean {
    const store = this.getAllRaw();
    if (store && key in store) {
      this.set(key, null);
      return true;
    }

    return false;
  }

  flush(): boolean {
    this.storage.clear();

    return true;
  }

  getAll(): { [p: string]: T } {
    const content: T = this.getAllRaw();

    if (!content) {
      return null;
    }

    return Object
      .keys(content)
      .map((key) => ({ [key]: content[key].value }))
      .reduce((current, next) => ({
        ...current,
        ...next,
      }));
  }

  getAllRaw(): T {
    return JSON.parse(this.storage.getItem(this.version()));
  }

  load(): boolean {
    return true;
  }

  get<S>(key: string): S {
    const json = this.getAllRaw();

    if (!json || !(key in json)) {
      return null;
    }

    return json[key].value;
  }

  save(): boolean {
    return true;
  }

  set<S>(key: string, value: S, ttl: number = null): boolean {
    const content = this.getAllRaw() ?? {};

    this.storage.setItem(this.version(),
      JSON.stringify(
        mergeDeep(content, {
          [key]: {
            value,
            created: +new Date(),
            ttl: ttl === null ? ttl : Number(process.env.VUE_APP_STORAGE_TTL_MS),
          },
        }),
      ));

    return true;
  }

  version(): string {
    return process.env.VUE_APP_STORAGE_PREFIX.concat('_').concat(COMMIT_HASH);
  }

  cleanup(): void {
    Object.keys(this.storage).forEach((key) => {
      if (key.startsWith(process.env.VUE_APP_STORAGE_PREFIX) && key !== this.version()) {
        this.storage.removeItem(key);
      }
    });
  }

  constructor() {
    super();
    this.cleanup();
  }

  isSupported(): boolean {
    const testKeyValue = 'g-portal-test-localstorage';

    try {
      this.storage.setItem(testKeyValue, testKeyValue);
      this.storage.removeItem(testKeyValue);

      return true;
    } catch (e) {
      return false;
    }
  }

  isAvailable(): boolean {
    return this.isSupported();
  }
}
